import { Country } from 'store/common/models';

export const EU_ISO = 'EU';

const ISO_ALIASES: Record<string, string> = {
  UK: 'GB',
  EU: EU_ISO,
};

/** Non-ISO / multi-country labels that Intl region names cannot resolve. */
const SPECIAL_REGION_NAMES: Record<string, string> = {
  'european union': EU_ISO,
  europe: EU_ISO,
  eu: EU_ISO,
  'الاتحاد الأوروبي': EU_ISO,
};

const SPECIAL_REGION_LABELS: Record<string, Record<string, string>> = {
  [EU_ISO]: {
    en: 'European Union',
    'en-US': 'European Union',
    ar: 'الاتحاد الأوروبي',
    es: 'Unión Europea',
    fr: 'Union européenne',
    it: 'Unione Europea',
    pl: 'Unia Europejska',
    ro: 'Uniunea Europeană',
    sr: 'Evropska unija',
    sq: 'Bashkimi Evropian',
    el: 'Ευρωπαϊκή Ένωση',
  },
};

const displayNamesCache = new Map<string, Intl.DisplayNames | null>();

function toIntlLocale(language: string): string {
  return language.replace('_', '-');
}

function normalizeIso(iso: string): string {
  const upper = iso.toUpperCase();
  return ISO_ALIASES[upper] ?? upper;
}

/**
 * European Union is a meta-region used only for OSS returns.
 * It must not appear in general country dropdowns (entities, billing, etc.).
 */
export function isEuropeanUnionCountryIsoOrName(
  isoOrName: string | undefined | null
): boolean {
  if (!isoOrName) return false;
  const trimmed = isoOrName.trim();
  if (!trimmed) return false;
  if (normalizeIso(trimmed) === EU_ISO) return true;
  return SPECIAL_REGION_NAMES[trimmed.toLowerCase()] === EU_ISO;
}

export function isEuropeanUnionCountry(country: {
  iso?: string | null;
  name?: string | null;
  countryName?: string | null;
}): boolean {
  return (
    isEuropeanUnionCountryIsoOrName(country.iso) ||
    isEuropeanUnionCountryIsoOrName(country.name) ||
    isEuropeanUnionCountryIsoOrName(country.countryName)
  );
}

export function withoutEuropeanUnion<
  T extends {
    iso?: string | null;
    name?: string | null;
    countryName?: string | null;
  }
>(countries: T[] | undefined | null): T[] {
  if (!countries?.length) return [];
  return countries.filter((country) => !isEuropeanUnionCountry(country));
}

function getRegionDisplayNames(locale: string): Intl.DisplayNames | null {
  if (!displayNamesCache.has(locale)) {
    try {
      displayNamesCache.set(
        locale,
        new Intl.DisplayNames([locale], { type: 'region' })
      );
    } catch {
      displayNamesCache.set(locale, null);
    }
  }

  return displayNamesCache.get(locale) ?? null;
}

export function getCountryLabel(
  iso: string | undefined,
  fallbackName: string | undefined,
  language: string
): string {
  if (!iso) {
    return fallbackName ?? '-';
  }

  const normalizedIso = normalizeIso(iso);
  const specialLabels = SPECIAL_REGION_LABELS[normalizedIso];
  if (specialLabels) {
    const lang = toIntlLocale(language);
    const short = lang.split('-')[0];
    return (
      specialLabels[lang] ??
      specialLabels[short] ??
      specialLabels.en ??
      fallbackName ??
      iso
    );
  }

  const displayNames = getRegionDisplayNames(toIntlLocale(language));

  if (displayNames) {
    try {
      const localized = displayNames.of(normalizedIso);
      if (localized && localized !== normalizedIso) {
        return localized;
      }
    } catch {
      // Fall back to the API-provided English name.
    }
  }

  return fallbackName ?? iso;
}

export function findCountry(
  isoOrName: string | undefined,
  countries: Country[] | undefined
): Country | undefined {
  if (!isoOrName || !countries?.length) {
    return undefined;
  }

  const trimmed = isoOrName.trim();
  const upper = trimmed.toUpperCase();
  const lower = trimmed.toLowerCase();

  return (
    countries.find((country) => country.iso?.toUpperCase() === upper) ??
    countries.find((country) => country.iso3?.toUpperCase() === upper) ??
    countries.find((country) => country.name?.toLowerCase() === lower)
  );
}

export function getCountryLabelFromCountries(
  isoOrName: string | undefined,
  countries: Country[] | undefined,
  language: string,
  fallbackName?: string
): string {
  if (isoOrName) {
    const specialCode = SPECIAL_REGION_NAMES[isoOrName.trim().toLowerCase()];
    if (specialCode) {
      return getCountryLabel(specialCode, isoOrName, language);
    }
  }

  const country = findCountry(isoOrName, countries);
  if (country) {
    return getCountryLabel(country.iso, country.name, language);
  }

  if (isoOrName) {
    return getCountryLabel(isoOrName, fallbackName ?? isoOrName, language);
  }

  return fallbackName ?? '-';
}

export function localizeCountryInCompositeLabel(
  label: string | undefined,
  countries: Country[] | undefined,
  language: string
): string {
  if (!label) {
    return '';
  }

  const separatorIndex = label.indexOf(' - ');
  if (separatorIndex === -1) {
    return getCountryLabelFromCountries(label, countries, language, label);
  }

  const countryPart = label.slice(0, separatorIndex);
  const remainder = label.slice(separatorIndex + 3);
  const localizedCountry = getCountryLabelFromCountries(
    countryPart,
    countries,
    language,
    countryPart
  );

  return `${localizedCountry} - ${remainder}`;
}
